import { BrzFrame, ContextChangeEvent } from '@brz/brz-frame';
import i18nInstance from '@localization/i18n';
import { logger } from '@utils/logging-utils';
import type { ReactNode } from 'react';
import { useCallback } from 'react';

import styles from './AppFrame.module.scss';
import { useMenuItems } from './useMenuItems';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@state/hooks';
import {
  selectOrganizationRolePermissions,
  setBrz8FinanceEnabled,
  setBrzFinanceEnabled,
  setRootorganizationId,
  setSelectedLicenseOwnerId,
  setSelectedOrganizationId
} from '@state/app-context-slice';
import { AppFrameSettingsDialogContent } from './AppFrameSettingsDialogContent/AppFrameSettingsDialogContent';
import { useAuth } from '@hooks/useAuth';

type AppFrameProps = {
   children: ReactNode;
};

function AppFrameSettingsDialogComponent(): ReactNode {
  return <AppFrameSettingsDialogContent />;
}

export function AppFrame({ children }: Readonly<AppFrameProps>) {
  const dispatch = useAppDispatch();
  const menuItems = useMenuItems();
  const { t }= useTranslation();
  const { logout } = useAuth();
  const organizationRolePermissions = useAppSelector(selectOrganizationRolePermissions);

  const updateOrganizationContextState = useCallback(
    (organization?: ContextChangeEvent['organization'] | null) => {
      if (!organization?.id || !organization.licenseOwnerId) {
        dispatch(setSelectedLicenseOwnerId(''));
        dispatch(setSelectedOrganizationId(''));
        dispatch(setRootorganizationId(''));
        dispatch(setBrzFinanceEnabled(false));
        dispatch(setBrz8FinanceEnabled(false));
        return;
      }

      const selectedOrganization = organizationRolePermissions?.find(org => org.organizationId === organization.id);
      const rootOrganizationId =
        organizationRolePermissions?.find(org => org.licenseOwnerId === organization.licenseOwnerId && org.isRootOrganization)?.organizationId ?? '';

      dispatch(setSelectedLicenseOwnerId(organization.licenseOwnerId));
      dispatch(setSelectedOrganizationId(organization.id));
      dispatch(setRootorganizationId(rootOrganizationId));
      dispatch(setBrzFinanceEnabled(Boolean(selectedOrganization?.brZ365Finance)));
      dispatch(setBrz8FinanceEnabled(Boolean(selectedOrganization?.brZ8Finance)));
    },
    [dispatch, organizationRolePermissions]
  );

  const handleContextChange = useCallback(
    (context: ContextChangeEvent) => {
      if (context.changeType !== 'organization') {
        return;
      }

      updateOrganizationContextState(context.organization ?? null);
    },
    [updateOrganizationContextState]
  );

  const onLanguageChange = useCallback(
    (lng: string) => {
      i18nInstance.changeLanguage(lng);
      logger.info('App', 'Language changed', { lng });
    },
    []
  );

  return (
    <BrzFrame
      appName={t('layouts:title')}
      onLanguageChange={onLanguageChange}
      oldStyle
      items={menuItems}
      onContextChange={handleContextChange}
      settingsDialogComponent={AppFrameSettingsDialogComponent}
      logoutHandler={logout}
    >
      <div className={styles.childContent}>
        {children}
      </div>
    </BrzFrame>
  );
}
